import { useEffect, useRef, useState } from "react";
import Button from "../../components/button/Button"
import { Spiner } from "../../components/spinner/Spiner";
import TableList, { GridCol, GridRow } from "../../components/table/TableList"
import { auth } from "../../firebase";
import companiesQuery from "../../firestore/Companies.query";
import { ButtonSettingItems, newReplyButton, ReplyButton } from "../../model/ReplyButton";
import { ReplyButtonDeleteModal } from "./ReplyButtonDeleteModal";
import { ReplyButtonsModal } from "./ReplyButtonsModal";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";

export const ReplyButtonPage = () => {
  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const dispatch = useDispatch();

  const isDisabled = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isLoad, setIsLoad] = useState(true);
  const [replyButtonList, setReplyButtonList] = useState<ReplyButton[]>([]);
  const [replyButton, setReplyButton] = useState<ReplyButton>(newReplyButton);
  const [deleteReplyButton, setDeleteReplyButton] = useState<ReplyButton | undefined>();

  useEffect(() => {
    const f = async () => {
      setIsLoad(true);
      // 返信ボタン一覧取得
      const replyButtonList = await companiesQuery.getReplyButtons(currentUser?.tenantId!);
      setReplyButtonList(replyButtonList);
      setIsLoad(false);
    };
    f();
  }, [currentUser?.tenantId, isOpen, dialogIsOpen]);

  const columns: GridCol[] = [
    {
      field: "number",
      headerName: "No.",
      style: "w-1/12",
    },
    {
      field: "name",
      headerName: "設定名",
      style: "w-4/12",
    },
    {
      field: "description",
      headerName: "ボタン名",
      style: "w-5/12",
    },
    {
      field: "control",
      headerName: "編集/削除",
      style: "w-2/12",
      sort: false
    },
  ];
  const rowList: GridRow[] = replyButtonList.map((replyButton, index) => {
    return {
      rowData: {
        number: index + 1,
        name: (<div className={"sm:whitespace-normal"}>{replyButton.name}</div>),
        description: (<div className={"sm:whitespace-normal"}>{replyButton.buttonName.join(" ／ ")}</div>),
        control: (
          <>
            <div className="flex space-x-2">
              <Button
              className={"whitespace-nowrap"}
                onClick={() => {
                  setIsUpdate(true);
                  setReplyButton(replyButton);
                  toggle();
                }}
                children={"編集"}/>
              <Button
                color="red"
                onClick={() => {
                  deleteDialogToggle();
                  setDeleteReplyButton(replyButton);
                }}
                className={"whitespace-nowrap"}
                children={"削除"}/>
            </div>
          </>
        )
      },
      sortableRowData: {
        name: replyButton.name,
        description: replyButton.buttonName.join("/")
      }
    };
  });

  const onSubmit = async (
    data: ReplyButton & ButtonSettingItems
  ) => {
    if(!isDisabled.current){
      try {
        // 返信ボタン一覧取得
        let replyButtons = await companiesQuery.getReplyButtons(currentUser?.tenantId!);
        // 編集対象のグループ以外のグループを取得
        replyButtons = replyButtons.filter(button => button.id !== data.id)
        // グループ名称が一致するグループを取得し重複チェックを行う
        replyButtons = replyButtons.filter(button => button.name === data.name)
        if (replyButtons.length > 0){
          // 重複した場合、dupCheckをtrueにする
          sessionStorage.setItem("dupCheck", "true");
        }
        // 重複しない場合、グループ保存処理実行
        if(sessionStorage.getItem("dupCheck") === "false"){
          isDisabled.current = true;
          data.buttonName = [data.buttonName1, data.buttonName2 ?? '回答2', data.buttonName3 ?? '回答3'];
          data.buttonColor.splice(data.buttonNumber);
          data.buttonName.splice(data.buttonNumber);

          const value: ReplyButton = {
            id: replyButton?.id ?? '',
            tenantId: currentUser?.tenantId!,
            name: data.name,
            buttonNumber: data.buttonNumber,
            buttonName: data.buttonName,
            buttonColor: data.buttonColor,
            type: "municipality",
            createUser: currentUser?.uid!,
            createAt: replyButton?.createAt ?? new Date()
          }

          if(isUpdate) {
            await companiesQuery.updateReplyButton(currentUser?.tenantId!, value);
          } else {
            data.createUser = currentUser?.uid!;
            data.createAt = new Date();
            await companiesQuery.setReplyButton(currentUser?.tenantId!, value);
          }
          dispatch(alertOpen(AlertValue(ConstMessage.SUCCESS_SAVE_REPLY_BUTTONS, "success")));
          toggle();
          isDisabled.current = false;
        }
      } catch (err: any) {
        let errorMessage = err.code;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILURE_TO_NETWORK_CONNECTION, "danger")));
        } else {
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILURE_TO_SAVE_REPLY_BUTTONS, "danger")));
        }
        console.error(err);
        isDisabled.current = false;
      }
    }
  };

  const toggle = () => {
    setIsOpen(!isOpen);
  };
  const deleteDialogToggle = () => {
    setDialogIsOpen(!dialogIsOpen);
  };

  const onDelete = (replyButtonId: string) => {
    if(!isDisabled.current){
      try {
        isDisabled.current = true;
        deleteDialogToggle();
        companiesQuery.deleteReplyButton(currentUser?.tenantId!, replyButtonId);
        dispatch(alertOpen(AlertValue(ConstMessage.SUCCESS_DELETE_REPLY_BUTTONS, "success")));
        isDisabled.current = false;
      } catch (err: any) {
        let errorMessage = err.code;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILURE_TO_NETWORK_CONNECTION, "danger")));
        } else {
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILURE_TO_SAVE_REPLY_BUTTONS, "danger")));
        }
        console.error(err);
        isDisabled.current = false;
      }
    }
  }

  return (
    <div className="h-full">
      <ReplyButtonsModal
        children={undefined}
        open={isOpen}
        toggle={toggle}
        closeOnClickOutside={false}
        updateFlg={isUpdate}
        replyButton={replyButton}
        authorUid={currentUser?.uid!}
        onSubmit={onSubmit}
      />
      <ReplyButtonDeleteModal
        children={undefined}
        open={dialogIsOpen}
        toggle={deleteDialogToggle}
        closeOnClickOutside={false}
        replyButton={deleteReplyButton}
        handle={onDelete}
      />
      <div className="sm:flex px-6 pt-6 pb-4 sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-xl font-semibold text-gray-900">
            返信ボタン設定
          </h2>
        </div>
        <div>
          <Button
            size={"sm"}
            onClick={() => {
              setIsUpdate(false);
              setReplyButton(newReplyButton);
              toggle();
            }}
            children={"返信ボタン新規作成"}
          />
        </div>
      </div>
      {isLoad === true ? <Spiner /> : (
      <div className={"px-6"}>
        <TableList
          header
          gridCol={columns}
          gridRow={rowList}
          defaultSortOrder={"desc"}
          limit={50}
        />
      </div>
    )}
    </div>
  );
};